import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Stethoscope, 
  Heart, 
  Baby, 
  Eye, 
  Ear, 
  Activity,
  Calendar,
  Award,
  Phone,
  ArrowLeft,
  MapPin,
  Globe,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for doctor specializations
const getSpecializationIcon = (iconType) => {
  const iconClass = "w-20 h-20 text-primary";
  
  switch (iconType) {
    case 'dental':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2C7.5 2 6 5 6 8c0 2 .5 3 1 4.5.5 1.5 1 3 1 5.5 0 2 1 4 2.5 4s2-1.5 2.5-3.5c.5 2 1 3.5 2.5 3.5s2.5-2 2.5-4c0-2.5.5-4 1-5.5S18 10 18 8c0-3-1.5-6-6-6z"/>
        </svg>
      );
    case 'eye':
      return <Eye className={iconClass} />;
    case 'maternal':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="5"/>
          <path d="M12 13c-4 0-7 2-7 5v2h14v-2c0-3-3-5-7-5z"/>
          <circle cx="12" cy="8" r="2"/>
        </svg>
      );
    case 'pediatric':
      return <Baby className={iconClass} />;
    case 'physiotherapy':
      return <Activity className={iconClass} />;
    case 'ent':
      return <Ear className={iconClass} />;
    case 'stethoscope':
    default:
      return <Stethoscope className={iconClass} />;
  }
};

export default function DoctorProfile() {
  const { id } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        const response = await axios.get(`${API}/doctors/${id}`);
        setDoctor(response.data);
      } catch (err) {
        console.error('Error fetching doctor:', err);
        setError('Doctor not found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDoctor();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !doctor) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-slate-600 text-lg">Doctor not found</p>
        <Link to="/doctors">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Doctors
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary to-primary-700 py-16 md:py-20" data-testid="doctor-profile-hero">
        <div className="container-custom">
          <Link to="/doctors" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors" data-testid="doctor-back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to All Doctors
          </Link>
          
          <div className="grid md:grid-cols-3 gap-8 items-center">
            {/* Doctor Icon/Photo */}
            <div className="flex justify-center md:justify-start">
              <div className="w-40 h-40 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center border-4 border-white/20">
                {doctor.image ? (
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  getSpecializationIcon(doctor.icon_type)
                )}
              </div>
            </div>
            
            {/* Doctor Info */}
            <div className="md:col-span-2 text-center md:text-left">
              <span className="inline-block px-4 py-1.5 bg-accent text-white text-sm rounded-full mb-4">
                {doctor.specialty_code || doctor.title}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                {doctor.name}
              </h1>
              <p className="text-white/90 text-xl mb-4">
                {doctor.specialty}
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-white/80">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{doctor.department_name}</span>
                </div>
                {doctor.languages && doctor.languages.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    <span>{doctor.languages.join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16" data-testid="doctor-profile-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* About */}
              <Card>
                <CardContent className="p-6 md:p-8">
                  <h2 className="font-heading text-xl font-bold text-primary mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    About
                  </h2>
                  <p className="text-slate-600 leading-relaxed">
                    {doctor.bio || `${doctor.name} is a dedicated healthcare professional at Baobab Hospital, specializing in ${doctor.specialty}. With a commitment to patient-centered care, they provide quality medical services to our community.`}
                  </p>
                </CardContent>
              </Card>

              {/* Qualifications */}
              {doctor.qualifications && doctor.qualifications.length > 0 && (
                <Card>
                  <CardContent className="p-6 md:p-8">
                    <h2 className="font-heading text-xl font-bold text-primary mb-4 flex items-center gap-2">
                      <Award className="w-5 h-5" />
                      Qualifications
                    </h2>
                    <ul className="space-y-3">
                      {doctor.qualifications.map((qual, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{qual}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Available Days */}
              {doctor.available_days && doctor.available_days.length > 0 && (
                <Card>
                  <CardContent className="p-6 md:p-8">
                    <h2 className="font-heading text-xl font-bold text-primary mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Availability
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {doctor.available_days.map((day, index) => (
                        <span
                          key={index}
                          className="px-4 py-2 bg-primary/10 text-primary rounded-lg text-sm font-medium"
                        >
                          {day}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Column - CTA */}
            <div className="space-y-6">
              {/* Book Appointment Card */}
              <Card className="bg-gradient-to-br from-primary to-primary-700 text-white sticky top-24">
                <CardContent className="p-6 md:p-8">
                  <h3 className="font-heading text-xl font-bold mb-4">
                    Book an Appointment
                  </h3>
                  <p className="text-white/80 mb-6 text-sm">
                    Schedule a consultation with {doctor.name}. Our team is ready to assist you with your healthcare needs.
                  </p>
                  <Link to="/book-appointment" data-testid="doctor-book-appointment">
                    <Button className="w-full bg-white text-primary hover:bg-slate-100 rounded-lg">
                      <Calendar className="w-4 h-4 mr-2" />
                      Book Appointment
                    </Button>
                  </Link>
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <a href="tel:+255659984488" className="flex items-center justify-center gap-2 text-white/90 hover:text-white transition-colors">
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">+255 659 984 488</span>
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Department Info */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-primary mb-3">
                    Department
                  </h3>
                  <p className="text-slate-600 mb-4">{doctor.department_name}</p>
                  <Link to="/departments" className="text-primary hover:text-accent text-sm font-medium transition-colors">
                    View All Departments
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-accent to-accent-soft" data-testid="doctor-profile-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Need Medical Assistance?
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            Our medical team is ready to provide you with excellent healthcare. Contact us today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-appointment">
              <Button size="lg" className="bg-white text-primary hover:bg-slate-100 rounded-lg px-8">
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Button>
            </Link>
            <Link to="/doctors">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-lg px-8">
                View All Doctors
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
